// src/utils/utilsExaustor.js
/**
 * Utilitários e regras de negócio para Exaustores
 * Contém formatters, parsers e validações
 */

// ==================== FORMATTERS ====================

/**
 * Converte segundos em string formatada HH:MM:SS
 * @param {number} seconds - Segundos totais
 * @returns {string} Tempo formatado ou mensagem padrão
 */
export const formatTime = (seconds) => {
  if (seconds === null || typeof seconds === "undefined") {
    return "Sem temporização";
  }
  if (seconds <= 0) return "00:00:00";
  
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  
  return `${h.toString().padStart(2, "0")}:${m
    .toString()
    .padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

/**
 * Converte string "HH:MM:SS" | "MM:SS" | "SS" em segundos
 * @param {string} str - String de tempo
 * @returns {number|null} Segundos ou null se inválido
 */
export const parseTimeToSeconds = (str) => {
  if (!str || typeof str !== "string") return null;
  
  const parts = str.trim().split(":").map((p) => p.trim());
  
  // Valida se todos os parts são números
  if (parts.some((p) => p === "" || isNaN(Number(p)))) return null;

  let h = 0,
    m = 0,
    s = 0;

  if (parts.length === 3) {
    [h, m, s] = parts.map(Number);
  } else if (parts.length === 2) {
    [m, s] = parts.map(Number);
  } else if (parts.length === 1) {
    [s] = parts.map(Number);
  } else {
    return null;
  }

  // Valida ranges
  if (m >= 60 || s >= 60 || h < 0 || m < 0 || s < 0) return null;
  
  return h * 3600 + m * 60 + s;
};

/**
 * Converte segundos em string para input (HH:MM:SS)
 * @param {number} seconds - Segundos totais
 * @returns {string} String formatada para input
 */
export const secondsToTimeString = (seconds) => {
  if (typeof seconds !== "number" || seconds < 0) return "00:00:00";
  
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  
  return `${h.toString().padStart(2, "0")}:${m
    .toString()
    .padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

/**
 * Extrai número de "Exaustor X" para ordenação
 * @param {string|number} idStr - ID do exaustor
 * @returns {number} Número extraído ou 0
 */
export const getExaustorNumber = (idStr) => {
  if (typeof idStr !== "string") return Number(idStr) || 0;
  const m = idStr.match(/\d+/);
  return m ? Number(m[0]) : 0;
};

/**
 * Mapeia estado da unidade do backend para CircleStatus
 * @param {string} state - Estado da unidade (running, expired, off)
 * @returns {string} Estado mapeado para componente visual (on, expired, off)
 */
export const mapUnitStateToCircle = (state) => {
  if (state === "running") return "on";
  if (state === "expired") return "expired";
  return "off";
};

// ==================== REGRAS DE NEGÓCIO ====================

/**
 * Verifica se deve mostrar mensagem de modo manual
 * @param {Object} exaustor - Objeto do exaustor
 * @returns {boolean}
 */
export const shouldShowManualMessage = (exaustor) => {
  return Boolean(exaustor?.modoManual);
};

/**
 * Verifica se deve mostrar mensagem de modo dependente
 * @param {Object} exaustor - Objeto do exaustor
 * @returns {boolean}
 */
export const shouldShowDependenteMessage = (exaustor) => {
  return Boolean(exaustor?.modoDependente);
};

/**
 * Verifica se deve mostrar lista de unidades
 * @param {Object} exaustor - Objeto do exaustor
 * @returns {boolean}
 */
export const shouldShowUnidades = (exaustor) => {
  // Não mostra se está em modo manual
  if (shouldShowManualMessage(exaustor)) return false;
  
  // ✅ Não mostra se está em modo dependente
  if (shouldShowDependenteMessage(exaustor)) return false;
  
  // Mostra se tem unidades
  return Array.isArray(exaustor?.unidades) && exaustor.unidades.length > 0;
};

/**
 * Retorna texto do botão baseado no estado
 * @param {Object} exaustor - Objeto do exaustor
 * @returns {string} "Ligar" ou "Desligar"
 */
export const getExaustorButtonText = (exaustor) => {
  return exaustor?.status === "on" ? "Desligar" : "Ligar";
};

/**
 * Retorna classe CSS do botão baseado no estado
 * @param {Object} exaustor - Objeto do exaustor
 * @returns {string} Classe CSS
 */
export const getExaustorButtonClass = (exaustor) => {
  return exaustor?.status === "on" 
    ? "buttonExaustor-desligar" 
    : "buttonExaustor-ligar";
};

/**
 * Verifica se exaustor deve permanecer ligado (regras especiais)
 * @param {Object} exaustor - Objeto do exaustor
 * @returns {boolean}
 */
export const shouldKeepExaustorOn = (exaustor) => {
  // Permanece ligado se modo dependente
  if (exaustor?.modoDependente) return true;
  
  // Permanece ligado se status "on" e tem timer de desligamento
  if (
    exaustor?.status === "on" &&
    typeof exaustor.timerDesligamento === "number" &&
    exaustor.timerDesligamento > 0
  ) {
    return true;
  }
  
  return false;
};

/**
 * Retorna qual timer deve ser exibido (principal ou desligamento)
 * @param {Object} exaustor - Objeto do exaustor
 * @returns {number} Valor do timer a ser exibido
 */
export const getDisplayTimer = (exaustor) => {
  if (!exaustor) return 0;
  
  // Se status on, timer zerado, mas tem timerDesligamento, mostra ele
  if (
    exaustor.status === "on" &&
    (exaustor.timer === 0 || typeof exaustor.timer !== "number") &&
    typeof exaustor.timerDesligamento === "number" &&
    exaustor.timerDesligamento > 0
  ) {
    return exaustor.timerDesligamento;
  }
  
  return exaustor.timer ?? 0;
};

/**
 * Ordena exaustores por número e coloca sem unidades no final
 * @param {Array} exaustores - Array de exaustores
 * @returns {Array} Array ordenado
 */
export const sortExaustores = (exaustores) => {
  return [...exaustores]
    .sort((a, b) => getExaustorNumber(b.id) - getExaustorNumber(a.id))
    .sort((a, b) => (a.unidades?.length === 0 ? 1 : -1));
};

/**
 * Ordena unidades por ID numérico
 * @param {Array} unidades - Array de unidades
 * @returns {Array} Array ordenado
 */
export const sortUnidades = (unidades) => {
  return [...unidades].sort((a, b) => Number(a.id) - Number(b.id));
};

/**
 * Valida se timer está zerado e exaustor deve desligar
 * @param {Object} exaustor - Objeto do exaustor
 * @returns {boolean}
 */
export const shouldTurnOffDueToTimer = (exaustor) => {
  if (!exaustor) return false;
  
  // NÃO desliga se tem regras especiais
  if (shouldKeepExaustorOn(exaustor)) return false;
  
  // Desliga se status off OU timer <= 0
  return exaustor.status === "off" || (exaustor.timer ?? 0) <= 0;
};

/**
 * Verifica se alguma unidade está ativa
 * @param {Array} unidades - Array de unidades
 * @returns {boolean}
 */
export const hasActiveUnits = (unidades) => {
  if (!Array.isArray(unidades)) return false;
  return unidades.some((u) => u.state === "running" || u.state === "expired");
};

/**
 * Retorna tipo de modo manual para mensagem
 * @param {string|boolean} modoManual - Modo manual (false, "ManualBT", "ManualPC")
 * @returns {string} "manualBT" ou "manualPC"
 */
export const getManualType = (modoManual) => {
  if (modoManual === "manualBT") return "manualBT";
  if (modoManual === "manualPC") return "manualPC";
  return "manualPC"; // fallback
};

/**
 * Determina qual tipo de mensagem deve ser exibida
 * Facilita a extensão para novos tipos de mensagens
 * @param {Object} exaustor - Objeto do exaustor
 * @returns {Object|null} { type: string, data: Object } ou null se não houver mensagem
 */
export const getMessageType = (exaustor) => {
  // ✅ Prioridade das mensagens (ordem importa!)
  
  // 1. Modo Dependente (MAIOR PRIORIDADE - sempre mostra quando ativo)
  if (exaustor?.modoDependente) {
    return {
      type: "dependente",
      data: { mestres: exaustor.mestresAtivos || [] },
    };
  }
  
  // 2. Modo Manual (só mostra se NÃO estiver em modo dependente)
  if (exaustor?.modoManual) {
    return {
      type: "manual",
      data: { modoManual: exaustor.modoManual },
    };
  }
  
  // 🆕 FUTURO: Adicionar novas condições aqui
  
  // 3. Modo Manutenção (exemplo futuro)
  // if (exaustor?.modoManutencao) {
  //   return {
  //     type: "manutencao",
  //     data: {
  //       tecnico: exaustor.tecnicoManutencao,
  //       dataInicio: exaustor.dataInicioManutencao,
  //     },
  //   };
  // }
  
  // 4. Modo Emergência (exemplo futuro)
  // if (exaustor?.modoEmergencia) {
  //   return {
  //     type: "emergencia",
  //     data: { motivo: exaustor.motivoEmergencia },
  //   };
  // }
  
  // 5. Agendado (exemplo futuro)
  // if (exaustor?.agendado && !exaustor?.status === "on") {
  //   return {
  //     type: "agendado",
  //     data: {
  //       horaInicio: exaustor.horaInicioAgendado,
  //       horaFim: exaustor.horaFimAgendado,
  //     },
  //   };
  // }
  
  // Nenhuma mensagem especial
  return null;
};

// ==================== VALIDAÇÕES ====================

/**
 * Valida estrutura de dados do exaustor
 * @param {Object} exaustor - Objeto do exaustor
 * @returns {boolean}
 */
export const isValidExaustor = (exaustor) => {
  if (!exaustor || typeof exaustor !== "object") return false;
  if (!exaustor.id) return false;
  return true;
};

/**
 * Valida se string de timer é válida
 * @param {string} timerStr - String do timer
 * @returns {boolean}
 */
export const isValidTimerString = (timerStr) => {
  return parseTimeToSeconds(timerStr) !== null;
};